import React, { useEffect, useRef, useState } from "react";
import {
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Save,
  Upload,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";

import useClientConfig from "../../../Services/ClientServices/ClientConfig/useClientConfig";

const API_IMAGE_BASE_URL = "https://api.anlook.com";
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;

const EMPTY_FORM = {
  section_title: "",
  section_image: "",
  section_description: "",

  title1: "",
  desc1: "",

  title2: "",
  desc2: "",

  title3: "",
  desc3: "",

  title4: "",
  desc4: "",

  title5: "",
  desc5: "",

  title6: "",
  desc6: "",
};

const normalizeChooseUsData = (data) => {
  const row = Array.isArray(data) ? data[0] || {} : data || {};

  return {
    section_title: row?.section_title || "",
    section_image: row?.section_image || "",
    section_description: row?.section_description || "",

    title1: row?.title1 || "",
    desc1: row?.desc1 || "",

    title2: row?.title2 || "",
    desc2: row?.desc2 || "",

    title3: row?.title3 || "",
    desc3: row?.desc3 || "",

    title4: row?.title4 || "",
    desc4: row?.desc4 || "",

    title5: row?.title5 || "",
    desc5: row?.desc5 || "",

    title6: row?.title6 || "",
    desc6: row?.desc6 || "",
  };
};

const getImageUrl = (imagePath) => {
  if (!imagePath) {
    return "";
  }

  const cleanPath = String(imagePath).trim();

  if (
    cleanPath.startsWith("http://") ||
    cleanPath.startsWith("https://") ||
    cleanPath.startsWith("data:") ||
    cleanPath.startsWith("blob:")
  ) {
    return cleanPath;
  }

  return `${API_IMAGE_BASE_URL}/${cleanPath.replace(/^\/+/, "")}`;
};

export default function ChooseUsSection({ clientId }) {
  const { getChooseUs, updateChooseUs, uploadChooseUsImage } =
    useClientConfig();

  const fileInputRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);

  const [selectedImage, setSelectedImage] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(false);

  const previewImageUrl = localPreviewUrl || getImageUrl(form.section_image);

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setLocalPreviewUrl("");
    setImageError(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image size must be less than 2 MB");
      event.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    setSelectedImage(file);
    setLocalPreviewUrl(objectUrl);
    setImageError(false);

    event.target.value = "";
  };

  const loadChooseUsDetails = async () => {
    if (!clientId) {
      setForm(EMPTY_FORM);
      clearSelectedImage();
      return;
    }

    setLoading(true);

    try {
      const response = await getChooseUs(clientId);

      setForm(normalizeChooseUsData(response));
      clearSelectedImage();
    } catch (error) {
      console.error("Failed to load Choose Us details:", error);
      setForm(EMPTY_FORM);
      clearSelectedImage();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchChooseUsDetails = async () => {
      if (!clientId) {
        if (isMounted) {
          setForm(EMPTY_FORM);
          setSelectedImage(null);
          setLocalPreviewUrl("");
          setImageError(false);
        }

        return;
      }

      if (isMounted) {
        setLoading(true);
      }

      try {
        const response = await getChooseUs(clientId);

        if (isMounted) {
          setForm(normalizeChooseUsData(response));
          setSelectedImage(null);
          setLocalPreviewUrl("");
          setImageError(false);
        }
      } catch (error) {
        console.error("Failed to load Choose Us details:", error);

        if (isMounted) {
          setForm(EMPTY_FORM);
          setSelectedImage(null);
          setLocalPreviewUrl("");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchChooseUsDetails();

    return () => {
      isMounted = false;
    };

  }, [clientId]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!clientId || saving || loading || uploadingImage) {
      return;
    }

    if (!form.section_title.trim()) {
      toast.error("Section title is required");
      return;
    }

    setSaving(true);

    try {
      let finalSectionImage = form.section_image?.trim() || "";

      if (selectedImage) {
        setUploadingImage(true);

        const uploadedImageUrl = await uploadChooseUsImage({
          userid: clientId,
          file: selectedImage,
        });

        setUploadingImage(false);

        if (!uploadedImageUrl) {
          toast.error(
            "Image upload failed. Choose Us section was not updated.",
          );
          return;
        }

        finalSectionImage = uploadedImageUrl;

        setForm((previous) => ({
          ...previous,
          section_image: uploadedImageUrl,
        }));

        clearSelectedImage();
      }

      const success = await updateChooseUs({
        userid: clientId,

        section_title: form.section_title.trim(),
        section_image: finalSectionImage,
        section_description: form.section_description.trim(),

        title1: form.title1.trim(),
        desc1: form.desc1.trim(),

        title2: form.title2.trim(),
        desc2: form.desc2.trim(),

        title3: form.title3.trim(),
        desc3: form.desc3.trim(),

        title4: form.title4.trim(),
        desc4: form.desc4.trim(),

        title5: form.title5.trim(),
        desc5: form.desc5.trim(),

        title6: form.title6.trim(),
        desc6: form.desc6.trim(),
      });

      if (success) {
        await loadChooseUsDetails();
      }
    } catch (error) {
      console.error("Failed to update Choose Us details:", error);

      toast.error("Failed to save Choose Us details");
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  if (!clientId) {
    return (
      <div className="rounded-2xl border border-dashed bg-white px-6 py-12 text-center">
        <h3 className="text-base font-semibold text-gray-900">
          Client not selected
        </h3>

        <p className="mt-1 text-sm text-gray-500">
          Select a client to manage the Choose Us section.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Choose Us Section
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              Manage the main content, image and six feature points displayed in
              the website Choose Us section.
            </p>
          </div>

          <button
            type="button"
            onClick={loadChooseUsDetails}
            disabled={loading || saving || uploadingImage}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center rounded-2xl border bg-white">
          <div className="text-center">
            <Loader2
              size={32}
              className="mx-auto animate-spin text-[#01C37D]"
            />

            <p className="mt-3 text-sm text-gray-500">
              Loading Choose Us details...
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Main section */}
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="mb-5">
              <h4 className="font-semibold text-gray-900">
                Main section content
              </h4>

              <p className="mt-1 text-sm text-gray-500">
                Add the main heading, section image and description.
              </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
              <div className="space-y-4">
                {/* Section title */}
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-gray-700">
                    Section title
                  </span>

                  <input
                    type="text"
                    name="section_title"
                    value={form.section_title}
                    onChange={handleChange}
                    placeholder="Why choose us?"
                    className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10"
                  />
                </label>

                {/* Image upload */}
                <div>
                  <span className="mb-1.5 block text-sm font-medium text-gray-700">
                    Section image
                  </span>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                  />

                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={saving || uploadingImage}
                        className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white transition hover:bg-[#00ad70] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={16} />
                            Select Image
                          </>
                        )}
                      </button>

                      {selectedImage ? (
                        <div className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-700">
                              {selectedImage.name}
                            </p>

                            <p className="mt-0.5 text-xs text-gray-400">
                              {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={clearSelectedImage}
                            disabled={saving || uploadingImage}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Remove selected image"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs font-medium text-gray-600">
                            PNG, JPG, JPEG or WEBP
                          </p>

                          <p className="mt-0.5 text-xs text-gray-400">
                            Maximum image size 2 MB
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="mt-3 text-xs text-gray-500">
                      Image Save Choose Us button click karne par upload hogi.
                    </p>
                  </div>

                  {form.section_image && !selectedImage && (
                    <div className="mt-2 rounded-xl bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-400">Current image</p>

                      <p className="mt-0.5 break-all text-xs font-medium text-gray-700">
                        {form.section_image}
                      </p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-gray-700">
                    Section description
                  </span>

                  <textarea
                    name="section_description"
                    value={form.section_description}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Enter a short description about why customers should choose this business..."
                    className="w-full resize-y rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10"
                  />
                </label>
              </div>

              {/* Image preview */}
              <div>
                <span className="mb-1.5 block text-sm font-medium text-gray-700">
                  Image preview
                </span>

                <div className="relative flex min-h-[260px] items-center justify-center overflow-hidden rounded-2xl border border-dashed bg-gray-50">
                  {previewImageUrl && !imageError ? (
                    <>
                      <img
                        src={previewImageUrl}
                        alt={form.section_title || "Choose Us preview"}
                        onLoad={() => setImageError(false)}
                        onError={() => setImageError(true)}
                        className="h-[260px] w-full object-cover"
                      />

                      {selectedImage && (
                        <span className="absolute left-3 top-3 rounded-full bg-[#01C37D] px-3 py-1 text-xs font-medium text-white shadow">
                          New image
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="px-6 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-gray-400 shadow-sm">
                        <ImageIcon size={26} />
                      </div>

                      <p className="mt-3 text-sm font-medium text-gray-600">
                        {imageError
                          ? "Unable to load this image"
                          : "Select an image to preview"}
                      </p>

                      {imageError && (
                        <p className="mt-1 break-all text-xs text-gray-400">
                          {previewImageUrl}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Six points */}
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="mb-5">
              <h4 className="font-semibold text-gray-900">Choose Us points</h4>

              <p className="mt-1 text-sm text-gray-500">
                Add up to six title and description points.
              </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {Array.from({ length: 6 }, (_, index) => {
                const number = index + 1;
                const titleKey = `title${number}`;
                const descriptionKey = `desc${number}`;

                return (
                  <div
                    key={number}
                    className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4"
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#01C37D] text-sm font-semibold text-white">
                        {number}
                      </span>

                      <div>
                        <h5 className="text-sm font-semibold text-gray-900">
                          Choose Us Point {number}
                        </h5>

                        <p className="text-xs text-gray-500">
                          Add a title and supporting description.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block">
                        <span className="mb-1.5 block text-xs font-medium text-gray-600">
                          Title
                        </span>

                        <input
                          type="text"
                          name={titleKey}
                          value={form[titleKey]}
                          onChange={handleChange}
                          placeholder={`Enter point ${number} title`}
                          className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10"
                        />
                      </label>

                      <label className="block">
                        <span className="mb-1.5 block text-xs font-medium text-gray-600">
                          Description
                        </span>

                        <textarea
                          name={descriptionKey}
                          value={form[descriptionKey]}
                          onChange={handleChange}
                          rows={4}
                          placeholder={`Enter point ${number} description`}
                          className="w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10"
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Save button */}
          <div className="sticky bottom-3 z-10 flex justify-end rounded-2xl p-4">
            <button
              type="submit"
              disabled={saving || loading || uploadingImage}
              className="inline-flex h-11 min-w-[190px] items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#00ad70] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploadingImage ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Uploading image...
                </>
              ) : saving ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={17} />
                  Save Choose Us
                </>
              )}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
